import { Injectable } from '@angular/core';
import { PatientFileService } from 'src/app/patients/patient/patient-file/patient-file.service';
import { DerfsService } from 'src/app/questionnaires/derfs/derfs.service';
import { DryEyeMedicalHistoryFormGroup } from 'src/app/shared/consult-forms/dry-eye-forms/dry-eye-medical-history-form/dry-eye-medical-history-form.model';
import { MedicalHistoryPopulator } from '../medical-history-source.model';

/**
 * Applies the patient profile's non-modifiable data through the DERFS schema
 * engine (map conditions / mapsTo / transformValue).
 */
@Injectable({ providedIn: 'root' })
export class NonModifiablePopulator implements MedicalHistoryPopulator {
  constructor(
    private derfsService: DerfsService,
    private patientFileService: PatientFileService
  ) {}

  populate(formGroup: DryEyeMedicalHistoryFormGroup, nonModifiableData: any): void {
    if (!nonModifiableData) {
      return;
    }

    formGroup.controls['previousOcularSurgeryFreeText'].patchValue('');
    Object.keys(nonModifiableData).forEach(key => {
      const schema = this.derfsService.flattenedDerfsSchema[key];
      if (!schema) {
        return;
      }
      if (schema.mapsTo && schema.mapsTo[0] === 'previousOcularSurgeryFreeText') {
        formGroup.controls['previousOcularSurgeryFreeText'].setValue(
          this.patientFileService.transformPastOcularHistoryValue(
            formGroup.controls['previousOcularSurgeryFreeText'].value,
            schema.description,
            nonModifiableData[key]
          )
        );
      } else if (formGroup['controls'][key]) {
        this.mapDerfsToMedicalHistory(formGroup, schema, nonModifiableData[key], key);
      }
      if (schema.mapsTo && schema.mapsTo[1] && formGroup['controls'][schema.mapsTo[1]]) {
        // Hacky way to deal with toggleable fields. Guard the companion control:
        // some DERFS schema entries name a mapsTo[1] that is not a control here.
        formGroup['controls'][schema.mapsTo[1]].patchValue(nonModifiableData[key + 'Value']);
      }
    });
    formGroup.markAsDirty();
  }

  private mapDerfsToMedicalHistory(
    formGroup: DryEyeMedicalHistoryFormGroup,
    schemaData: any,
    responseDataValue: any,
    key?: string
  ): void {
    if (schemaData.mapConditions === undefined) {
      return;
    }
    const mapConditionsLength = schemaData.mapConditions.length;
    for (let i = 0; i < mapConditionsLength; i++) {
      const patientFileSchema = this.patientFileService.flattenedPatientFileSchema[key];
      if (key && patientFileSchema && patientFileSchema.isCheckbox) {
        // Directly maps checkboxes to checkboxes, bypassing map conditions to handle
        // unchecking a checkbox.
        this.applyMapping(formGroup, schemaData, i, responseDataValue);
      } else if (this.mapConditionSatisfied(schemaData.mapConditions[i], responseDataValue)) {
        this.applyMapping(formGroup, schemaData, i, responseDataValue);
      } else if (schemaData.defaultMapValue) {
        formGroup.controls[schemaData.mapsTo[i]].setValue(schemaData.defaultMapValue[i]);
      }
    }
  }

  private applyMapping(
    formGroup: DryEyeMedicalHistoryFormGroup,
    schemaData: any,
    mapConditionIndex: number,
    responseDataValue: any
  ): void {
    const control = formGroup.controls[schemaData.mapsTo[mapConditionIndex]];
    if (!control) {
      return;
    }
    if (schemaData.mapValues) {
      control.setValue(schemaData.mapValues[mapConditionIndex]);
    } else if (schemaData.transformValue) {
      control.setValue(schemaData.transformValue(responseDataValue, control.value));
    } else {
      control.setValue(responseDataValue);
    }
  }

  private mapConditionSatisfied(mapCondition: any, val: any): boolean {
    return (
      val !== undefined &&
      (val === mapCondition ||
        (typeof val === 'number' && val >= mapCondition) ||
        (mapCondition === '' && (typeof val === 'string' || Array.isArray(val)) && val.length >= 1))
    );
  }
}
